// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Overblik (Bygning først) as data: the "Sagen" panel's rows (with the BEK
 * 496 plan deadline), the KPI panel, the affaldshierarki bars, "Næste
 * skridt" with where each one leads, and
 * "Seneste" with Danish relative dates. The figures are the old Overblik's
 * (`overblik/model.ts` `kpis`, `circularitySegments`), read off server
 * responses; this module only picks, orders and words them.
 *
 * Pure: no React, no DOM.
 */

import {
  TREATMENTS,
  parseServerUtc,
  type ProjectInfo,
  type ReportPdfVersion,
  type Sample,
  type SurveyFractions,
  type SurveySummary,
  type SurveyType,
  type Treatment,
} from '../api/types';
import { RESULT_LABEL, TREATMENT_LABEL, formatNumber } from '../kortlaegning/vocab';
import { circularitySegments, danishDate, kpis, type Kpi } from '../overblik/model';
import type { ModeId } from './modes';
import {
  REQUIRED_FOR_REPORT,
  SAGSDATA_FIELDS,
  bekNote,
  daysNote,
  daysUntil,
  deadline,
  draftOf,
  formatArea,
  isIsoDate,
  missingFields,
  parseArea,
  type SagsFieldSpec,
  type SagsGroupId,
  type SagsKey,
  type SagsdataExtras,
} from './sagsdata';
import { INDBERETNING_PAGE, type Selection } from './selection';
import { pointsMissingText } from './submission';

// --------------------------------------------------------------- KPIs ------

/** The panel's figures: total mass first (prototype), then the old Overblik's five (`kpis`). */
export function panelKpis(s: SurveySummary): Kpi[] {
  const mass: Kpi = { key: 'mass', value: formatNumber(s.total_mass_t), unit: 't', label: 'Samlet masse' };
  return [mass, ...kpis(s)];
}

// --------------------------------------------------------- hierarchy -------

export interface HierarchyRow {
  treatment: Treatment;
  label: string;
  tonnes: number;
  /** Whole percent (largest remainder over all steps, as `circularitySegments`). */
  percent: number;
  /** The colour as a token name: `--circ-<treatment>`. */
  token: string;
  /** Tonnes of the other steps: the empty part of the row's track. */
  rest: number;
}

/** Every waste-hierarchy step, best first, even those without tonnes (prototype). */
export function hierarchyRows(circularity: Record<Treatment, number>): HierarchyRow[] {
  const segments = circularitySegments(circularity);
  const tonnes = TREATMENTS.map((t) => Math.max(0, circularity[t] ?? 0));
  const total = tonnes.reduce((a, b) => a + b, 0);
  return TREATMENTS.map((t, i) => ({
    treatment: t,
    label: TREATMENT_LABEL[t],
    tonnes: tonnes[i],
    percent: segments.find((s) => s.treatment === t)?.percent ?? 0,
    token: `--circ-${t}`,
    rest: total - tonnes[i],
  }));
}

// ------------------------------------------------------------- sagen -------

export interface SagenRow {
  /** A Sagsdata field, or `plan`: the computed plan deadline. */
  key: SagsKey | 'plan';
  group: SagsGroupId;
  label: string;
  /** The value as shown; null when the field is empty. */
  value: string | null;
  /** "om 5 dage", "fristen er overskredet". */
  note?: string;
  tone?: 'warn' | 'crit';
  /** The report needs it (`REQUIRED_FOR_REPORT`): an empty one reads "mangler", not hidden. */
  required: boolean;
}

/** A field's draft text as the panel shows it; null when empty. */
function sagenValue(spec: SagsFieldSpec, text: string): string | null {
  const t = text.trim();
  if (t === '') return null;
  // A legacy free-text survey date is shown as stored (`danishDate`).
  if (spec.kind === 'date') return danishDate(t);
  if (spec.kind === 'number') {
    const area = parseArea(t)?.value ?? null;
    const bek = bekNote(area);
    return area !== null && bek ? `${formatArea(area)} · ${bek.short}` : t;
  }
  return t;
}

/**
 * Overblik's "Sagen" panel: every Sagsdata field but the name (the panel's
 * title line), in form order with its group, the value as shown (dates in
 * Danish, the floor area with what it means under BEK 496) or null when
 * empty. Right after a valid demolition start comes the plan deadline (start
 * − 14 days), with how near it is. The panel decides what an empty row shows.
 */
export function sagenRows(project: ProjectInfo | null | undefined, extras: SagsdataExtras, now: Date = new Date()): SagenRow[] {
  const draft = draftOf(project, extras);
  const rows: SagenRow[] = [];
  for (const spec of SAGSDATA_FIELDS) {
    if (spec.key === 'name') continue;
    const text = draft[spec.key];
    rows.push({
      key: spec.key,
      group: spec.group,
      label: spec.label,
      value: sagenValue(spec, text),
      required: REQUIRED_FOR_REPORT.includes(spec.key),
    });
    const plan = spec.key === 'demolition_start' ? deadline(text) : null;
    if (plan) {
      const row: SagenRow = { key: 'plan', group: spec.group, label: 'Plan til kommunen senest', value: danishDate(plan), required: false };
      const days = daysUntil(plan, now);
      const n = days === null ? null : daysNote(days);
      if (n) {
        row.note = n.note;
        if (n.tone) row.tone = n.tone;
      }
      rows.push(row);
    }
  }
  return rows;
}

// ------------------------------------------------------- næste skridt ------

export type StepTone = 'warn' | 'good' | 'wait';

/** Where a step leads: a mode, optionally with a focus (`setSel({ mode, ...patch })`). */
export interface StepTarget {
  mode: ModeId;
  patch?: Partial<Pick<Selection, 'sampleId' | 'typeId' | 'page' | 'edit'>>;
}

export interface NextStep {
  key: 'queue' | 'samples' | 'report' | 'indberetning' | 'sagsdata';
  /** The badge: a count, a version ("v3"), "✓", "–" or "…". */
  badge: string;
  text: string;
  /** The accessible name: the badge in words where it is a figure. */
  label: string;
  tone: StepTone;
  target: StepTarget;
}

export interface NextStepOptions {
  /** The live queue count (the shell's survey counts) — wins over the summary's. */
  queue?: number | null;
  /** `GET /survey/fractions`: adds the Indberetning step when present. */
  fractions?: SurveyFractions | null;
  /** The Sagsdata extras (server or local copy) for the completeness step. */
  extras?: SagsdataExtras;
  /**
   * The next submission to bygningsaffald.dk (`bygning/submission.ts`
   * `Submission.next`): when present, the Indberetning step names the part
   * and what it lacks (worded as Rapport's `nextSubmissionText`) instead of
   * the fractions' blockers.
   */
  submission?: { part: 1 | 2; missing: number } | null;
}

function step(key: NextStep['key'], badge: string, text: string, tone: StepTone, target: StepTarget): NextStep {
  const label = /^\d+$/.test(badge) ? `${badge} ${text}` : /^v\d+$/.test(badge) ? `Version ${badge.slice(1)}: ${text}` : text;
  return { key, badge, text, label, tone, target };
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * What waits in the case, in the prototype's order: types awaiting review →
 * Gennemgang; samples without an answer → Miljø (the first open sample in
 * focus); the latest report version → Rapport; the next submission to
 * bygningsaffald.dk (Del 1 / Del 2 and how many points it lacks, when given;
 * else what blocks the fractions, when known) → Rapport › Indberetning; whether Sagsdata is
 * complete → the Sagsdata editor over Overblik (`?edit=sag`). `samples` is
 * null until loaded (then the summary's pending count is used); `versions`
 * null means still loading.
 */
export function nextSteps(
  summary: SurveySummary | null,
  samples: readonly Sample[] | null,
  versions: readonly ReportPdfVersion[] | null,
  project: ProjectInfo | null,
  options: NextStepOptions = {},
): NextStep[] {
  const out: NextStep[] = [];

  const queue = options.queue ?? summary?.counts.queue ?? null;
  const all = summary?.counts.all ?? null;
  if (queue === null) out.push(step('queue', '…', 'Kortlægningen indlæses', 'wait', { mode: 'gennemgang' }));
  else if (all === 0 && queue === 0) out.push(step('queue', '–', 'Ingen kortlægning endnu', 'wait', { mode: 'ressourcer' }));
  else if (queue > 0) {
    out.push(step('queue', String(queue), plural(queue, 'type venter på godkendelse', 'typer venter på godkendelse'), 'warn', { mode: 'gennemgang' }));
  } else out.push(step('queue', '✓', 'Alle typer er gennemgået', 'good', { mode: 'gennemgang' }));

  const open = samples ? samples.filter((s) => s.stage !== 'svar') : null;
  const pending = open ? open.length : (summary?.pending_samples ?? null);
  if (pending === null) out.push(step('samples', '…', 'Prøverne indlæses', 'wait', { mode: 'miljoe' }));
  else if (pending > 0) {
    const target: StepTarget = open && open.length > 0 ? { mode: 'miljoe', patch: { sampleId: open[0].id } } : { mode: 'miljoe' };
    out.push(step('samples', String(pending), plural(pending, 'prøve uden svar', 'prøver uden svar'), 'warn', target));
  } else if (samples && samples.length === 0) out.push(step('samples', '–', 'Ingen prøver registreret', 'wait', { mode: 'miljoe' }));
  else out.push(step('samples', '✓', samples ? 'Alle prøvesvar modtaget' : 'Ingen prøver afventer svar', 'good', { mode: 'miljoe' }));

  if (versions === null) out.push(step('report', '…', 'Rapportversionerne indlæses', 'wait', { mode: 'rapport' }));
  else if (versions.length === 0) out.push(step('report', '–', 'Ingen rapport endnu', 'wait', { mode: 'rapport' }));
  else {
    const latest = versions.reduce((a, b) => (b.version > a.version ? b : a));
    const blocking = latest.blocking_types;
    const badge = `v${latest.version}`;
    if (blocking === 0) out.push(step('report', badge, 'Seneste rapport er komplet', 'good', { mode: 'rapport' }));
    else if (blocking === null) out.push(step('report', badge, 'Seneste rapport', 'wait', { mode: 'rapport' }));
    else {
      const text = `Rapport som kladde · ${blocking} ${plural(blocking, 'type blokerede', 'typer blokerede')}`;
      out.push(step('report', badge, text, 'wait', { mode: 'rapport' }));
    }
  }

  const fractions = options.fractions;
  const submission = options.submission;
  if (submission) {
    const target: StepTarget = { mode: 'rapport', patch: { page: INDBERETNING_PAGE } };
    const badge = `Del ${submission.part}`;
    const n = submission.missing;
    const text = n > 0 ? `${pointsMissingText(n)} til bygningsaffald.dk` : 'Klar til indberetning';
    out.push({ ...step('indberetning', badge, text, n > 0 ? 'warn' : 'good', target), label: `${badge}: ${text}` });
  } else if (fractions) {
    const target: StepTarget = { mode: 'rapport', patch: { page: INDBERETNING_PAGE } };
    const n = fractions.blocking_types;
    if (n > 0) out.push(step('indberetning', String(n), plural(n, 'type blokerer indberetningen', 'typer blokerer indberetningen'), 'warn', target));
    else out.push(step('indberetning', '✓', 'Klar til indberetning', 'good', target));
  }

  const missing = missingFields(project, options.extras ?? {});
  const editor: StepTarget = { mode: 'overblik', patch: { edit: 'sag' } };
  if (missing.length === 0) out.push(step('sagsdata', '✓', 'Sagsdata komplette', 'good', editor));
  else {
    const n = missing.length;
    out.push(step('sagsdata', String(n), plural(n, 'felt mangler i sagsdata', 'felter mangler i sagsdata'), 'warn', editor));
  }
  return out;
}

// ------------------------------------------------------------ seneste ------

export interface RecentItem {
  key: string;
  text: string;
  /** Danish relative date ("i går", "for 3 dage siden", "24.09"). */
  when: string;
  /** Epoch ms, for sorting. */
  at: number;
}

const DAY_MS = 86_400_000;

function localDayIndex(d: Date): number {
  return Math.round(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / DAY_MS);
}

/**
 * A Danish relative date: "lige nu", "for 5 min. siden", "for 3 timer
 * siden", "i går", "for 4 dage siden", then the date ("24.09", with the year
 * when it is not this year). `dateOnly` (a stored ISO date, no time) starts
 * at "i dag". A time in the future reads "lige nu" (clock skew).
 */
export function relativeDa(at: Date, now: Date = new Date(), dateOnly = false): string {
  const diff = now.getTime() - at.getTime();
  const days = localDayIndex(now) - localDayIndex(at);
  if (!dateOnly) {
    if (diff < 60_000) return 'lige nu';
    const min = Math.floor(diff / 60_000);
    if (min < 60) return `for ${min} min. siden`;
    if (days <= 0) {
      const h = Math.floor(min / 60);
      return h === 1 ? 'for 1 time siden' : `for ${h} timer siden`;
    }
  } else if (days <= 0) return 'i dag';
  if (days === 1) return 'i går';
  if (days < 7) return `for ${days} dage siden`;
  const dd = String(at.getDate()).padStart(2, '0');
  const mm = String(at.getMonth() + 1).padStart(2, '0');
  return at.getFullYear() === now.getFullYear() ? `${dd}.${mm}` : `${dd}.${mm}.${at.getFullYear()}`;
}

/** An ISO date as local midnight. */
function localDate(iso: string): Date | null {
  if (!isIsoDate(iso)) return null;
  const [y, m, d] = iso.trim().split('-').map(Number);
  return new Date(y, m - 1, d);
}

function typeText(t: SurveyType): string {
  if (t.review_status === 'approved') return `${t.name} godkendt`;
  if (t.review_status === 'rejected') return `${t.name} markeret som fejl`;
  return t.created_at !== '' && t.created_at === t.updated_at ? `${t.name} oprettet` : `${t.name} ændret`;
}

function sampleText(s: Sample): string {
  switch (s.stage) {
    case 'svar':
      return s.result ? `Prøvesvar ${s.code} modtaget · ${RESULT_LABEL[s.result].toLowerCase()}` : `Prøvesvar ${s.code} modtaget`;
    case 'sendt':
      return `${s.code} sendt til lab`;
    case 'udtaget':
      return `${s.code} udtaget`;
    case 'planlagt':
      return `${s.code} planlagt`;
  }
}

/**
 * The newest events in the case, newest first: types (by `updated_at`),
 * samples (by `updated_at`, worded by stage), report versions (by
 * `created_at`) and, when known, the client's approval of the screening.
 * Timestamps that do not parse are left out.
 */
export function recent(
  types: readonly SurveyType[],
  samples: readonly Sample[],
  versions: readonly ReportPdfVersion[] | null,
  now: Date = new Date(),
  extras: SagsdataExtras = {},
  limit = 5,
): RecentItem[] {
  const items: (RecentItem & { order: number })[] = [];
  const push = (key: string, text: string, at: Date | null, dateOnly = false) => {
    if (!at) return;
    items.push({ key, text, at: at.getTime(), when: relativeDa(at, now, dateOnly), order: items.length });
  };
  for (const t of types) push(`t${t.id}`, typeText(t), parseServerUtc(t.updated_at));
  for (const s of samples) push(`s${s.id}`, sampleText(s), parseServerUtc(s.updated_at));
  for (const v of versions ?? []) push(`v${v.id}`, `Rapport v${v.version} genereret`, parseServerUtc(v.created_at));
  if (extras.screening_approved_at) {
    push('screening', 'Bygherre godkendte screeningen', localDate(extras.screening_approved_at), true);
  }
  items.sort((a, b) => b.at - a.at || a.order - b.order);
  return items.slice(0, limit).map(({ key, text, when, at }) => ({ key, text, when, at }));
}
